import moment from 'moment-timezone'
import i18n from 'i18next'
import { buildPhotoURL, buildScreenshotURL } from '../http/ApiProvider'

export type NonViolatingReason = nonViolatingReasons | unclearCopyrightReason
export type ICaseType = 'Commercial' | 'Editorial'
export type BulkReviewCaseType = ICaseType | 'Multiple' | undefined

export enum EListType {
	'onHold',
	'inReview',
	'all',
	'reviewed',
	'awaitingReview',
}

export enum nonViolatingReasons {
	whitelisted = 'Whitelisted',
	singleSale = 'SingleSale',
	other = 'OtherReason',
	handout = 'Handout',
	noContactInformation = 'NoContactInformation',
}

export enum unclearCopyrightReason {
	unknownPhotographer = 'UnknownPhotographer',
	unknownDate = 'UnknownDate',
	outOfCopyright = 'OutOfCopyright',
	counterpartyOnPicture = 'CounterpartyOnPicture',
	other = 'OtherUnclearReason',
}

export type ICaseState =
	| 'stage_01'
	| 'stage_02'
	| 'stage_03'
	| 'client_approval'
	| 'contact_enrichment'
	| 'screenshot_review'
	| 'rejected'
	| 'on_hold'
	| 'ready_for_export'
	| 'pipedrive_open'
	| 'pipedrive_won'
	| 'pipedrive_lost'

type CaseTypeColorProps =
	| 'purple'
	| 'blue'
	| 'grey'
	| 'red'
	| 'yellow'
	| 'green'

export interface ICase {
	violation_id: string
	reg_date: string
	company_name?: string
	violation_type: ICaseType
	photo_id: string
	case_id: string
	days_remaining?: string
	comment: string
	photo_name?: string
	image_archive_url_pattern?: string
	is_violating: boolean
	on_hold: boolean
	non_violating_reason: NonViolatingReason | ''
	seen: boolean
	photographer_name: string
	state: ICaseState
	soft_claim: boolean
	url_raw: string
	occurred_at: string
	customer_approve_time: string
	screenshot_timestamp: string
	client_name?: string
	last_modified: string
	supplier: string
	timestamp?: string
	country: string
	domain: string
	registration_certificate_number: string | null
	registration_certificate_id: string | null
	transfer_agreement_id: string | null
	transfer_agreement_photographer_name: string | null
	client_image_id: string
	created_at?: string
	won_at?: string | null
	lost_at?: string | null
}

export interface ICaseLocation {
	type: string
	label: string
}

export const caseLocations: { [name: string]: ICaseLocation } = {
	inReview: {
		type: 'inReview',
		label: 'caseRow/labels/inReview',
	},
	onHold: {
		type: 'onHold',
		label: 'caseRow/labels/onHold',
	},
	createdCase: {
		type: 'createdCase',
		label: 'caseRow/labels/createdCase',
	},
}

export function updateCase(theCase: ICase, updateProps: Partial<ICase>): ICase {
	return {
		...theCase,
		...updateProps,
	}
}

export function getCaseLabelColor(type: string): CaseTypeColorProps {
	const colorsMap: Record<string, CaseTypeColorProps> = {
		Editorial: 'purple',
		Commercial: 'blue',
		'special-price': 'red',
		SoftClaim: 'yellow',
	}

	if (!colorsMap[type]) return 'grey'

	return colorsMap[type]
}

// Maps states to their translation key for grouping
// States with the same translation will be treated as one filter option
export const STATE_TO_TRANSLATIONKEY: Record<ICaseState, string> = {
	stage_01: 'caseState/stage_01',
	stage_02: 'caseState/stage_02',
	stage_03: 'caseState/stage_03',
	client_approval: 'caseState/open',
	on_hold: 'caseState/on_hold',
	pipedrive_won: 'caseState/pipedrive_won',

	// Closed
	pipedrive_lost: 'caseState/closed',
	rejected: 'caseState/closed',

	// Open
	screenshot_review: 'caseState/open',
	contact_enrichment: 'caseState/open',
	ready_for_export: 'caseState/open',
	pipedrive_open: 'caseState/open',
}

export function expandStateFilter(selectedStates: string[]): string[] {
	const selectedTranslations = new Set(
		selectedStates.map((state) => STATE_TO_TRANSLATIONKEY[state as ICaseState]),
	)

	const allStates = Object.keys(STATE_TO_TRANSLATIONKEY) as ICaseState[]
	return allStates.filter((state) =>
		selectedTranslations.has(STATE_TO_TRANSLATIONKEY[state]),
	)
}

export function getFormatOccurredDate(
	date: string,
	format = 'MMMM DD, YYYY',
): string {
	return moment(date).format(format)
}

export function getOriginalImageUrl(theCase: ICase, size = 100): string {
	return theCase.violation_id && buildPhotoURL(theCase.photo_id, size)
}

export function getArchiveImageUrl(theCase: ICase, size = 100): string {
	if (theCase.photo_name && theCase.image_archive_url_pattern)
		return theCase.image_archive_url_pattern.replace(
			'{{imageId}}',
			theCase.photo_name.split('.')[0],
		)

	return theCase.violation_id && buildPhotoURL(theCase.photo_id, size)
}

export function getScreenshotUrl(theCase: ICase, size = 100): string {
	return theCase.violation_id && buildScreenshotURL(theCase.violation_id, size)
}

export function getActionPromptPath(theCase: ICase): string {
	if (theCase.is_violating && !theCase.violation_type)
		return 'caseActions/pleaseChoose'
	if (!theCase.is_violating && !theCase.non_violating_reason)
		return 'caseActions/pleaseChoose'
	if (
		!theCase.is_violating &&
		(theCase.non_violating_reason === unclearCopyrightReason.other ||
			theCase.non_violating_reason === nonViolatingReasons.other) &&
		theCase.comment === ''
	)
		return 'caseActions/pleaseComment'
	return ''
}

export function getViolationLabel(theCase: ICase): string {
	if (!theCase.is_violating && theCase.non_violating_reason) {
		const reason = theCase.non_violating_reason
		const isOtherReason = Object.values(unclearCopyrightReason).some(
			(v) => v === reason,
		)

		if (isOtherReason) return i18n.t('caseRow/labels/UnclearReason')

		return i18n.t(`caseRow/labels/${reason}`)
	}
	if (theCase.violation_type) return theCase.violation_type

	return ''
}

export function isCaseInReview(theCase: ICase): boolean {
	return theCase.state === 'client_approval'
}

export function getCaseLocation(theCase: ICase): ICaseLocation {
	if (theCase.on_hold) return caseLocations.onHold
	return caseLocations.inReview
}

export default function createCase(props: Partial<ICase> = {}): ICase {
	let state = props.state || 'client_approval'
	if (!props.state && !props.is_violating && props.customer_approve_time)
		state = 'rejected'

	return {
		violation_id: props.violation_id || '',
		reg_date: props.reg_date || '',
		country: props.country || '',
		company_name: props.company_name || '-',
		violation_type: props.violation_type!,
		photo_id: props.photo_id || '',
		case_id: props.case_id || '',
		days_remaining: props.days_remaining || '',
		comment: props.comment || '',
		image_archive_url_pattern: props.image_archive_url_pattern || '',
		photo_name: props.photo_name ? `${props.photo_name}` : '',
		non_violating_reason: props.non_violating_reason || '',
		is_violating: !!props.is_violating,
		on_hold: !!props.on_hold,
		seen: !!props.seen,
		photographer_name: props.photographer_name || '-',
		supplier: props.supplier || '',
		state,
		soft_claim: !!props.soft_claim,
		url_raw: props.url_raw || '',
		occurred_at: props.occurred_at || '',
		customer_approve_time: props.customer_approve_time || '',
		last_modified: props.last_modified || '',
		screenshot_timestamp: props.screenshot_timestamp
			? moment
					.tz(
						props.screenshot_timestamp,
						Intl.DateTimeFormat().resolvedOptions().timeZone,
					)
					.format('MMM DD, YYYY [at] HH:mm z')
			: '-',
		client_name: props.client_name,
		domain: props.domain || '',
		registration_certificate_number:
			props.registration_certificate_number || '',
		registration_certificate_id: props.registration_certificate_id || '',
		client_image_id: props.client_image_id || '',
		transfer_agreement_id: props.transfer_agreement_id || null,
		transfer_agreement_photographer_name:
			props.transfer_agreement_photographer_name || null,
		won_at: props.won_at || null,
		lost_at: props.lost_at || null,
	}
}
